import { getDebtAprMetrics } from "@silo/utils";
import { DebtCurrentState, DebtMetrics, DebtNewState } from "./types";
import { Option, pipe } from "effect";
import {
  getAprState,
  getProjectionsState,
  getUtilizationState,
} from "../utils";
import { ProjectionsState } from "../types";
import { Percent } from "@silo/types";
import { isNullable } from "effect/Predicate";

interface GetDebtCurrentMetricsParams {
  currentState: DebtCurrentState;
  nativeRewardsSymbol: string;
  uopt: Percent;
  ucrit: Percent;
}

export function getDebtCurrentMetrics({
  currentState,
  nativeRewardsSymbol,
  uopt,
  ucrit,
}: GetDebtCurrentMetricsParams): DebtMetrics {
  const { utilization, debtBaseApr, projections, isDebtAprFixed } =
    currentState;
  const {
    hasDebtRewards,
    debtRewards,
    totalDebtRewardsApr,
    totalDebtApr,
    debtRewardsState,
    showFixedLabel,
  } = getDebtAprMetrics(currentState, nativeRewardsSymbol);

  const projectionsState = pipe(
    projections,
    Option.fromNullable,
    Option.map(({ debtBaseApr1d, debtBaseApr3d }) =>
      getProjectionsState({
        baseApr: debtBaseApr,
        baseApr1d: debtBaseApr1d,
        baseApr3d: debtBaseApr3d,
      })
    ),
    Option.getOrElse((): ProjectionsState => ({ _tag: "none" }))
  );

  const utilizationState = getUtilizationState({
    utilization,
    uopt,
    ucrit,
  });

  const aprState = getAprState(projectionsState, utilizationState);

  const currentMetrics: DebtMetrics = {
    utilization,
    debtBaseApr,
    hasDebtRewards,
    debtRewards,
    totalDebtRewardsApr,
    totalDebtApr,
    isDebtAprFixed,
    showFixedLabel,
    debtRewardsState,
    projectionsState,
    utilizationState,
    aprState,
  };

  return currentMetrics;
}

interface GetDebtNewMetricsParams {
  newState: DebtNewState | null;
  nativeRewardsSymbol: string;
  uopt: Percent;
  ucrit: Percent;
}

export function getDebtNewMetrics({
  newState,
  nativeRewardsSymbol,
  uopt,
  ucrit,
}: GetDebtNewMetricsParams): DebtMetrics | null {
  if (isNullable(newState)) return null;

  const {
    utilization,
    projections,
    debtRewardsApr,
    debtMerklRewards,
    isDebtAprFixed,
  } = newState;

  if (isNullable(projections)) return null;

  const { debtBaseApr, debtBaseApr1d, debtBaseApr3d } = projections;

  const {
    hasDebtRewards,
    debtRewards,
    totalDebtRewardsApr,
    totalDebtApr,
    debtRewardsState,
    showFixedLabel,
  } = getDebtAprMetrics(
    {
      debtBaseApr,
      debtRewardsApr,
      debtMerklRewards,
      isDebtAprFixed,
    },
    nativeRewardsSymbol
  );

  const projectionsState = getProjectionsState({
    baseApr: debtBaseApr,
    baseApr1d: debtBaseApr1d,
    baseApr3d: debtBaseApr3d,
  });

  const utilizationState = getUtilizationState({
    utilization,
    uopt,
    ucrit,
  });

  const aprState = getAprState(projectionsState, utilizationState);

  const newMetrics: DebtMetrics = {
    utilization,
    debtBaseApr,
    hasDebtRewards,
    debtRewards,
    totalDebtRewardsApr,
    totalDebtApr,
    isDebtAprFixed,
    showFixedLabel,
    debtRewardsState,
    projectionsState,
    utilizationState,
    aprState,
  };

  return newMetrics;
}
